/**
 * A figure that rolls rather than redraws.
 *
 * Only the digits that changed move — right to left, each one a frame behind
 * the last on the board, all together in a field — which is what makes a raise
 * read as a figure being counted up instead of a number being swapped out.
 * Everything else in the cell holds still, so the row underneath it does not
 * reflow while it counts.
 *
 * The rolling is done against the DOM directly. React owns what the figure is;
 * this owns the fifth of a second in which it becomes that, and the two would
 * only fight over the same nodes.
 */

import { useEffect, useRef, type ReactNode } from 'react';

import { formatMoneyCents } from '../lib/format';

/** The board's roll: long, staggered, always upward. Matches `--lb-digit`. */
const BOARD = { ms: 540, stagger: 34, ease: 'cubic-bezier(.22,1,.36,1)', fade: false } as const;

/**
 * A field's roll: one token duration, no stagger, and direction taken from the
 * change — a decrement rolls down, which is the only way a stepper's minus
 * button reads as a subtraction.
 */
const FIELD = {
  ms: 160,
  stagger: 0,
  ease: 'cubic-bezier(.215,.61,.355,1)',
  fade: true,
} as const;

export type RollVariant = 'board' | 'field';

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** Render a figure as still characters, each in its own box. */
function paint(host: HTMLElement, text: string): void {
  host.replaceChildren(
    ...Array.from(text, (character) => {
      const box = document.createElement('span');
      box.className = 'lb-char';
      const reel = document.createElement('span');
      reel.className = 'lb-reel';
      const digit = document.createElement('span');
      digit.className = 'lb-digit';
      digit.textContent = character;
      reel.appendChild(digit);
      box.appendChild(reel);
      return box;
    }),
  );
}

/**
 * Roll the changed characters from one figure to another.
 *
 * Both figures must be the same length; a figure that has grown a digit — a
 * bid crossing ten thousand — is painted outright, because rolling it would
 * mean animating characters that are not the same character in both.
 */
function roll(
  host: HTMLElement,
  from: string,
  to: string,
  timing: typeof BOARD | typeof FIELD,
  up: boolean,
): number {
  const boxes = host.querySelectorAll<HTMLElement>('.lb-char');
  const changed: number[] = [];
  for (let index = 0; index < to.length; index += 1) {
    if (from[index] !== to[index]) changed.push(index);
  }

  const away = 'translateY(calc(var(--lb-digit) * -1))';

  changed.forEach((index, order) => {
    const reel = boxes[index]?.firstElementChild as HTMLElement | undefined;
    if (reel === undefined) return;

    const outgoing = reel.firstElementChild as HTMLElement | null;
    const incoming = document.createElement('span');
    incoming.className = 'lb-digit';
    incoming.textContent = to[index] as string;

    const delay = (changed.length - 1 - order) * timing.stagger;
    reel.style.transition = 'none';
    if (up) {
      reel.appendChild(incoming);
      reel.style.transform = 'translateY(0)';
    } else {
      reel.insertBefore(incoming, reel.firstChild);
      reel.style.transform = away;
    }
    // Reading the layout is what commits the starting position; without it the
    // browser coalesces both writes and nothing moves.
    void reel.offsetHeight;
    reel.style.transition = `transform ${timing.ms}ms ${timing.ease} ${delay}ms`;
    reel.style.transform = up ? away : 'translateY(0)';

    if (timing.fade && outgoing !== null) {
      outgoing.style.transition = `opacity ${timing.ms}ms ${timing.ease} ${delay}ms`;
      outgoing.style.opacity = '0';
    }
  });

  return timing.ms + 80 + changed.length * timing.stagger;
}

/**
 * The roller itself, over any string of characters. The caller decides what
 * the figure says and which way a change travels.
 */
export function Reels({
  text,
  order,
  className,
  variant = 'board',
  label,
}: {
  readonly text: string;
  /** A number the change can be compared against, to pick a direction. */
  readonly order?: number;
  readonly className?: string;
  readonly variant?: RollVariant;
  /** What a screen reader is given instead of a column of characters. */
  readonly label?: string;
}): ReactNode {
  const host = useRef<HTMLSpanElement | null>(null);
  const shown = useRef<string | undefined>(undefined);
  const previousOrder = useRef<number | undefined>(undefined);
  const settle = useRef<number | undefined>(undefined);

  useEffect(() => {
    const element = host.current;
    if (element === null) return;

    const previous = shown.current;
    const wasAt = previousOrder.current;
    shown.current = text;
    previousOrder.current = order;

    // A roll still in flight has two digits in every changed reel and a
    // transform part-way through. Snap it to where it was going before
    // starting the next one, so rolls coalesce instead of stacking.
    if (settle.current !== undefined) {
      window.clearTimeout(settle.current);
      settle.current = undefined;
      if (previous !== undefined) paint(element, previous);
    }

    if (previous === undefined || previous === text) {
      paint(element, text);
      return;
    }
    if (prefersReducedMotion() || previous.length !== text.length) {
      paint(element, text);
      return;
    }

    const up = order === undefined || wasAt === undefined ? true : order >= wasAt;
    const after = roll(element, previous, text, variant === 'field' ? FIELD : BOARD, up);
    settle.current = window.setTimeout(() => paint(element, text), after);

    return () => {
      if (settle.current !== undefined) window.clearTimeout(settle.current);
    };
  }, [text, order, variant]);

  const classes = ['lb-amount', 'mono'];
  if (variant === 'field') classes.push('is-field');
  if (className !== undefined) classes.push(className);

  return (
    <span className={classes.join(' ')}>
      {/* The figure is also stated plainly, so a screen reader is given an
          amount rather than a column of separated characters. */}
      <span className="visually-hidden">{label ?? text}</span>
      <span className="lb-reels" ref={host} aria-hidden="true" />
    </span>
  );
}

export function Odometer({
  cents,
  className,
  variant = 'board',
}: {
  readonly cents: number;
  readonly className?: string;
  readonly variant?: RollVariant;
}): ReactNode {
  const text = formatMoneyCents(cents);
  return (
    <Reels
      text={text}
      variant={variant}
      {...(variant === 'field' ? { order: cents } : {})}
      {...(className === undefined ? {} : { className })}
    />
  );
}
